/**
 * Vehicle Card Component
 * Grid view card for vehicles list
 */

import { Link } from 'react-router-dom';
import { Car, User, Calendar, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import {
  Card,
  CardContent,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui';

export interface VehicleCardData {
  id: string;
  registration: string;
  make: string;
  model: string;
  year: number | null;
  color: string | null;
  customerName?: string;
  customerId?: string;
}

interface VehicleCardProps {
  vehicle: VehicleCardData;
  onEdit?: (vehicle: VehicleCardData) => void;
  onDelete?: (vehicle: VehicleCardData) => void;
}

export function VehicleCard({ vehicle, onEdit, onDelete }: VehicleCardProps) {
  return (
    <Card className="transition-all duration-200 hover:shadow-md hover:border-blue-200">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <Link
            to={`/vehicles/${vehicle.id}`}
            className="flex items-start gap-3 flex-1 min-w-0"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 flex-shrink-0">
              <Car className="h-5 w-5 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-mono font-semibold text-blue-600">{vehicle.registration}</p>
              <p className="text-sm text-gray-900 truncate">
                {vehicle.make} {vehicle.model}
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                {vehicle.year && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {vehicle.year}
                  </span>
                )}
                {vehicle.customerName && (
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    <span className="truncate">{vehicle.customerName}</span>
                  </span>
                )}
                {vehicle.color && (
                  <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                    {vehicle.color}
                  </span>
                )}
              </div>
            </div>
          </Link>

          {(onEdit || onDelete) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Acciones</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(vehicle)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Editar
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <DropdownMenuItem
                    onClick={() => onDelete(vehicle)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Eliminar
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Loading skeleton
export function VehicleCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 animate-pulse rounded-lg bg-gray-200" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
            <div className="h-3 w-32 animate-pulse rounded bg-gray-200" />
            <div className="h-3 w-20 animate-pulse rounded bg-gray-200" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
